import React from "react";
import { Modal } from "./Modal";
import { ModalBodyBuy } from "../ModalBodyBuy";
import { ethers } from "ethers";
import { saleAbi } from "../../blockchain/saleAbi";
import { erc20Abi } from "../../blockchain/erc20Abi";
import { cornAbi } from "../../blockchain/cornAbi"; // (미사용일 수 있음)
import { constants } from "../../blockchain/constants";
import { currencyById } from "../../blockchain/currencies";
import apiClient from "../../apiClient";
import { useTranslation } from "react-i18next";

/* ===================== helpers (모바일 안정화) ===================== */
const ZERO_ADDR = "0x0000000000000000000000000000000000000000";
const isZeroAddr = (a) => !!a && /^0x0{40}$/i.test(a);

const normalizeChainId = (cid) =>
  typeof cid === "string" && cid.startsWith("0x") ? parseInt(cid, 16) : Number(cid) || 0;

const getExpectedChainId = () =>
  Number((constants && (constants.CHAIN_ID || constants.KAIACHAIN_ID)) || 8217);

async function ensureCorrectChainIfPossible(ethereum) {
  const expected = getExpectedChainId();
  try {
    const raw = await ethereum.request({ method: "eth_chainId" });
    const cur = normalizeChainId(raw);
    if (cur === expected) return;

    const hexId = "0x" + expected.toString(16);
    try {
      await ethereum.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: hexId }],
      });
    } catch (err) {
      console.warn("wallet_switchEthereumChain failed:", err);
      // 필요하면 wallet_addEthereumChain 로직 추가
    }
  } catch (e) {
    console.warn("ensureCorrectChainIfPossible error:", e);
  }
}

// 주소/ID/네이티브 모든 케이스를 안전하게 해석
function resolveCurrencyInfo(item) {
  const rawAddr =
    item?.paymentToken || item?.currencyAddress || item?.price?.tokenAddress || item?.tokenAddress;

  if (rawAddr && /^0x[0-9a-fA-F]{40}$/.test(rawAddr)) {
    const a = rawAddr.toLowerCase();
    if (!isZeroAddr(a)) return { isNative: false, address: a, decimals: 18 };
    return { isNative: true, address: null, decimals: 18 };
  }

  const rawId = item?.currencyId ?? item?.currency ?? item?.price?.token?.symbol;
  let cur;
  try {
    if (typeof currencyById === "function") {
      cur = currencyById(rawId);
    } else if (currencyById) {
      cur =
        currencyById[String(rawId)] ||
        (String(rawId).toUpperCase ? currencyById[String(rawId).toUpperCase()] : undefined) ||
        (String(rawId).toLowerCase ? currencyById[String(rawId).toLowerCase()] : undefined);
    }
  } catch (_) {}

  if (typeof cur === "string") {
    const a = cur.toLowerCase();
    if (!isZeroAddr(a)) return { isNative: false, address: a, decimals: 18 };
    return { isNative: true, address: null, decimals: 18 };
  }

  if (cur && typeof cur === "object") {
    const addr = cur.tokenAddress || cur.address || cur.contract || (cur.token && cur.token.address) || null;
    const decimals = Number(cur.decimals != null ? cur.decimals : 18);
    const isNative =
      !!cur.isNative || !addr || isZeroAddr(String(addr)) || String(addr) === ethers.constants.AddressZero;
    return { isNative, address: isNative ? null : String(addr).toLowerCase(), decimals };
  }

  if (
    rawId === 1 ||
    rawId === "1" ||
    String(rawId).toUpperCase() === "KLAY" ||
    String(rawId).toUpperCase() === "NATIVE"
  ) {
    return { isNative: true, address: null, decimals: 18 };
  }
  return null;
}

function parsePriceBN(price, meta) {
  const s = String(price ?? "0");
  return meta.isNative ? ethers.utils.parseEther(s) : ethers.utils.parseUnits(s, meta.decimals || 18);
}

// 렌더 크래시 방지용 Error Boundary
class RenderGuard extends React.Component {
  constructor(props) {
    super(props);
    this.state = { err: null };
  }
  componentDidCatch(err, info) {
    console.error("[ModalBodyBuy render error]", err, info);
    this.setState({ err });
  }
  render() {
    if (this.state.err) {
      return (
        <div className="p-4 text-sm text-red-600">
          결제 UI 로딩 중 오류가 발생했습니다. (통화/네트워크 설정 확인)
        </div>
      );
    }
    return this.props.children;
  }
}
/* ============================================================ */

const BuyModal = ({
  setModalIsOpen,
  item,
  styledWrapper,
  setPageLoading,
  setForceRefresh,
  refreshPage,
  balance,
}) => {
  const { t } = useTranslation();

  // 통화 사전 해석 (실패 시 본문을 안전 안내로 대체)
  const cur = resolveCurrencyInfo(item);
  const canRenderBody = !!cur;

  return (
    <Modal
      setModalIsOpen={setModalIsOpen}
      title={t("common.buy")}
      btnTitle="Buy now"
      childrenBody={
        <RenderGuard>
          {canRenderBody ? (
            // ModalBodyBuy가 이 prop를 사용하지 않아도 무해합니다.
            <ModalBodyBuy item={item} balance={balance} resolvedCurrency={cur} />
          ) : (
            <div className="p-4 text-sm text-red-600">
              결제 통화를 해석할 수 없습니다. (currencyId/paymentToken 점검)
            </div>
          )}
        </RenderGuard>
      }
      styledWrapper={styledWrapper}
      onClick={async function () {
        try {
          const checkbox = document.getElementById("buyNowTerms");
          if (checkbox && !checkbox.checked) {
            alert("서비스 약관에 동의해주세요.");
            return;
          }
          if (item.saleStatus !== "ON_SALE") {
            alert("판매중이 아닙니다.");
            return;
          }

          if (!canRenderBody) {
            alert("지원하지 않는 결제 통화입니다. (currencyId/paymentToken 확인)");
            return;
          }

          const ethereum = window.ethereum;
          if (!ethereum) {
            alert("지갑이 연결되어 있지 않습니다.");
            return;
          }

          const provider = new ethers.providers.Web3Provider(ethereum);
          await provider.send("eth_requestAccounts", []);
          await ensureCorrectChainIfPossible(ethereum);

          const signer = provider.getSigner();
          const me = (await signer.getAddress())?.toLowerCase();

          if (me === (item.userAddress || "").toLowerCase()) {
            alert("본인의 NFT입니다.");
            return;
          }

          const saleContract = new ethers.Contract(constants.SALE, saleAbi, signer);

          // 가격 정규화
          const rawPrice = item?.price?.amount ?? item?.price ?? item?.finalPrice ?? item?.priceString;
          const payAmountBn = parsePriceBN(rawPrice, cur);

          const overrides = {};
          if (cur.isNative) {
            const bal = await provider.getBalance(me);
            if (bal.lt(payAmountBn)) {
              alert("잔액이 부족합니다.");
              return;
            }
            overrides.value = payAmountBn;
          } else {
            const erc20 = new ethers.Contract(cur.address, erc20Abi, signer);
            const [bal, allow] = await Promise.all([
              erc20.balanceOf(me),
              erc20.allowance(me, constants.SALE),
            ]);
            if (bal.lt(payAmountBn)) {
              alert("결제 토큰 잔액이 부족합니다.");
              return;
            }
            if (allow.lt(payAmountBn)) {
              setPageLoading(true);
              try {
                const txApprove = await erc20.approve(constants.SALE, payAmountBn);
                await txApprove.wait();
              } catch (e) {
                if ("increaseApproval" in erc20 && typeof erc20.increaseApproval === "function") {
                  const txApprove = await erc20.increaseApproval(constants.SALE, payAmountBn);
                  await txApprove.wait();
                } else {
                  setPageLoading(false);
                  throw e;
                }
              }
              setPageLoading(false);
            }
          }

          // 사전검증
          await saleContract.callStatic
            .buy(item.saleInfoId, 1, me, "0x", overrides)
            .catch((e) => {
              const msg =
                e?.reason || e?.errorName || e?.data?.message || e?.message || "구매 사전검증 실패";
              throw new Error(msg);
            });

          setPageLoading(true);

          // 가스 추정
          let gasLimit;
          try {
            gasLimit = await saleContract.estimateGas.buy(item.saleInfoId, 1, me, "0x", overrides);
          } catch (e) {
            setPageLoading(false);
            const msg =
              e?.reason || e?.errorName || e?.data?.message || e?.message || "가스 추정 실패(조건 불충족 가능)";
            throw new Error(msg);
          }

          const gasPrice = await provider.getGasPrice().catch(() => null);
          const tx = await saleContract.buy(item.saleInfoId, 1, me, "0x", {
            ...overrides,
            gasLimit: gasLimit.mul(12).div(10), // +20%
            ...(gasPrice ? { gasPrice, type: 0 } : {}),
          });

          await tx.wait();

          const response = await apiClient.post("blockchain/updateNft");
          if (response.data.result === "ok") {
            setTimeout(() => {
              refreshPage();
              setForceRefresh((prev) => !prev);
              setPageLoading(false);
              setModalIsOpen(false);
              alert("구매에 성공했습니다.");
            }, 1000);
          } else {
            setPageLoading(false);
            alert("구매는 완료되었으나 동기화가 지연되고 있습니다.");
          }
        } catch (e) {
          setPageLoading(false);
          console.error(e);
          alert(`mobile env debugging, error = ${e?.message || e}`);
        }
      }}
      onClickClose={function () {}}
    />
  );
};

export { BuyModal };
